import { Trans } from '@lingui/react/macro';
import type { ReactNode } from 'react';

import { Link, TextBody } from 'src/components';
import { LinkExternal } from 'src/components/LinkExternal';
import { Price } from 'src/components/Price';
import type { SKU } from 'src/utils/skus';
import { SUPPORT_URL } from 'src/utils/urls/supportUrls';

export interface FAQBlock {
  title: ReactNode;
  id: string;
  body: ReactNode;
}

type FAQConfig = {
  [group: string]: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [name: string]: (...args: any[]) => FAQBlock;
  };
};

export const FAQ = {
  account: {
    whatIsMFA: () => ({
      title: <Trans>What is multi-factor authentication?</Trans>,
      id: 'account.what-is-mfa',
      body: (
        <TextBody>
          <Trans>
            Multi-factor authentication (MFA) is an authentication method that
            requires you to provide two or more verification factors to log in
            to My reMarkable. This reduces the risk of someone being able to
            hack your account by getting hold of your password.
          </Trans>
        </TextBody>
      ),
    }),
    whatHappensIfIEnableMFA: () => ({
      title: <Trans>What happens if I enable MFA?</Trans>,
      id: 'account.what-is-happens-when-mfa-is-enabled',
      body: (
        <TextBody>
          <Trans>
            When you log in, you&rsquo;ll have to provide a security code from
            an authenticator app &mdash; like Authy or Google Authenticator
            &mdash; as well as your password. This helps us verify that
            it&rsquo;s really you trying to log in.
          </Trans>
        </TextBody>
      ),
    }),
  },
  connectWithSeats: {
    canOtherUsersGetAccessToMyDocuments: () => ({
      title: <Trans>Can other users get access to my documents?</Trans>,
      id: 'connectWithSeats.can-other-users-get-access-to-my-documents',
      body: (
        <TextBody>
          <Trans>
            No, only you can access your notes and documents. If you&apos;d like
            to share a document, you can send it by email.
          </Trans>
        </TextBody>
      ),
    }),
    invitedToConnectWithSeatsWillPriceChange: () => ({
      title: (
        <Trans>
          I&apos;ve been invited to a Connect subscription. Will the price of my
          subscription change?
        </Trans>
      ),
      id: 'connectWithSeats.invited-to-connect-with-seats-will-price-change',
      body: (
        <TextBody>
          <Trans>
            When you join a Connect subscription, the subscription fees will be
            covered by the owner of that subscription, and you won&apos;t need
            to make any payments.
          </Trans>
        </TextBody>
      ),
    }),
    whatHappensToMyFilesIfIJoin: () => ({
      title: <Trans>What happens to my files if I join?</Trans>,
      id: 'connectWithSeats.what-happens-to-my-files-if-i-join',
      body: (
        <TextBody>
          <Trans>
            Nothing will happen to your notes and files. All your documents will
            remain private, and you&apos;ll still have access to any files that
            have been synced to the cloud.
          </Trans>
        </TextBody>
      ),
    }),
    whatHappensToTeamMembersFiles: () => ({
      title: (
        <Trans>
          When new users join my subscription, do they keep their files?
        </Trans>
      ),
      id: 'connectWithSeats.what-happens-to-team-members-files',
      body: (
        <TextBody>
          <Trans>
            Yes. When a user joins your subscription, no changes are made to
            their files.
          </Trans>
        </TextBody>
      ),
    }),
    canIAccessTeamMembersFiles: () => ({
      title: (
        <Trans>
          When I invite new users to my subscription, can I access their files?
        </Trans>
      ),
      id: 'connectWithSeats.can-i-access-team-members-files',
      body: (
        <TextBody>
          <Trans>
            No, notes or documents are not shared when new members join your
            subscription. All files remain private. If a member leaves your
            subscription, they&apos;ll keep access to their files.
          </Trans>
        </TextBody>
      ),
    }),
    alreadyHaveConnect: () => ({
      title: (
        <Trans>
          I already have Connect. What happens to my current subscription?
        </Trans>
      ),
      id: 'connectWithSeats.already-have-connect',
      body: (
        <TextBody>
          <Trans>
            If you accept the invitation, your company will pay for your
            subscription. You&apos;ll still have access to all benefits and
            features that come with a regular Connect subscription.
          </Trans>
        </TextBody>
      ),
    }),
    whatHappensWithTrial: () => ({
      title: <Trans>What happens if I have a free trial?</Trans>,
      id: 'connectWithSeats.what-happens-with-trial',
      body: (
        <TextBody>
          <Trans>
            You&apos;ll keep your free trial if you join Connect. Once your
            trial ends, your employer will pay for your subscription. If you
            leave Connect, your free trial can&apos;t be resumed.
          </Trans>
        </TextBody>
      ),
    }),
    whyDoYouNeedMyCompanyDetails: () => ({
      title: <Trans>Why do you need my company details?</Trans>,
      id: 'why-do-you-need-my-company-details',
      body: (
        <span>
          <Trans>
            We need your company details to ensure accurate billing and to
            provide sales documentation.
          </Trans>
        </span>
      ),
    }),
    howDoesBillingWork: (sku?: SKU) => ({
      id: 'how-does-billing-work',
      title: <Trans>How does billing work?</Trans>,
      body: (
        <span>
          <Trans>
            The subscription price is linked to your team&apos;s size and
            updates automatically when you add or remove members. New users can
            be added for <Price sku={sku} />
            /mo. each, and are free until the next billing period. You
            won&apos;t be charged for members who have a free Connect trial. We
            accept cards and other digital payment methods.
          </Trans>
        </span>
      ),
    }),
    whenWillIBeCharged: () => ({
      id: 'when-will-i-be-charged',
      title: <Trans>When will I be charged?</Trans>,
      body: (
        <span>
          <Trans>
            You&apos;ll be charged on the same day each month as when you signed
            up for Connect. You can view your billing date in Payment history.
            You won&apos;t be charged for members who have a free Connect trial.
          </Trans>
        </span>
      ),
    }),
    whatHappensIfIRemoveAUser: () => ({
      id: 'what-happend-if-i-remove-a-user',
      title: (
        <Trans>What happens if I remove a user from my subscription?</Trans>
      ),
      body: (
        <span>
          <Trans>
            They will keep access to their files, but lose access to all
            subscription features and benefits. On the next billing date, you
            won&apos;t be charged for users who have been removed from your
            subscription.
          </Trans>
        </span>
      ),
    }),
    doYouOfferDeviceManagement: () => ({
      id: 'do-you-offer-device-management',
      title: <Trans>Do you offer device management?</Trans>,
      body: (
        <span>
          <Trans>
            Device management isn&apos;t currently available, but this is
            something we&apos;re working on.
          </Trans>
        </span>
      ),
    }),
    whatPaymentOptionsDoYouOffer: () => ({
      title: <Trans>What payment options do you offer?</Trans>,
      id: 'what-payment-options-do-you-offer',
      body: (
        <span>
          <Trans>
            We accept cards and other digital payment methods.
            <br />
            Under <b>Subscription</b>, you can change your payment method and
            find your payment history.
          </Trans>
        </span>
      ),
    }),
    whatDoesActiveSeatMean: () => ({
      id: 'what-does-active-seat-mean',
      title: <Trans>What does “active seat” mean?</Trans>,
      body: (
        <>
          <TextBody>
            <Trans>
              The number of active seats shows you how many users in your
              subscription have access to paid features.
              <br />
              On your monthly payment, you&apos;ll be billed for active seats.
            </Trans>
          </TextBody>
        </>
      ),
    }),
    whyWasMyPaymentDeclined: () => ({
      id: 'why-was-my-payment-declined',
      title: <Trans>Why was my payment declined?</Trans>,
      body: (
        <TextBody>
          <Trans>
            There are several reasons why a payment may be declined. Usually, it
            happens because the payment method lacked sufficient funds, or
            because the card has expired.
          </Trans>
        </TextBody>
      ),
    }),
    howOftenWillIBeChargedForConnectWithSeats: () => ({
      id: 'how-often-will-i-be-charged-for-connect-with-seats',
      title: <Trans>How often will I be charged?</Trans>,
      body: (
        <TextBody>
          <Trans>
            You&apos;ll be charged monthly or yearly, depending on your chosen
            billing schedule. Your billing date is the same date as when you
            subscribed to Connect.
          </Trans>
        </TextBody>
      ),
    }),
    whyWasMyBillingDifferentThanExpected: () => ({
      id: 'why-was-my-billing-different-than-expected',
      title: <Trans>Why was my billing different than I expected?</Trans>,
      body: (
        <TextBody>
          <Trans>
            The subscription price is linked to your team&apos;s size and
            updates automatically when you add or remove members. If you have
            unexpected charges, check if you&apos;ve added new members to your
            subscription.
          </Trans>
        </TextBody>
      ),
    }),
    willThePriceChangeWhenTheBetaPeriodEnds: () => ({
      id: 'will-the-price-change-when-the-beta-period-ends',
      title: <Trans>Will the price change when the beta period ends?</Trans>,
      body: (
        <TextBody>
          <Trans>
            The price and the terms of use may change after the beta period. You
            can cancel your subscription at any time.
          </Trans>
        </TextBody>
      ),
    }),
    asAnOwnerIDontNeedAccessToSubscriptionFeatures: () => ({
      id: 'as-an-owner-i-dont-need-access-to-subscription-features-can-i-remove-these',
      title: (
        <Trans>
          As an owner, I don&apos;t need access to subscription features. Can I
          remove these?
        </Trans>
      ),
      body: (
        <>
          <TextBody>
            <Trans>
              Yes, you can remove subscription features from your seat. Click
              Users, then Deactivate next to your email address in the members
              overview.
              <br />
              You&apos;ll then no longer have access to unlimited cloud storage,
              automatic sync, creating and editing in our apps, or the
              reMarkable Protection Plan.
            </Trans>
          </TextBody>
        </>
      ),
    }),
    whatIsTheDifferenceBetweenTheUserManagerAndOwnerRoles: () => ({
      id: 'what-is-the-difference-between-the-user-manager-and-owner-roles',
      title: (
        <Trans>
          What is the difference between the user manager and owner roles?
        </Trans>
      ),
      body: (
        <TextBody>
          <Trans>
            User managers and owners have different levels of access and
            permissions.
            <br />
            <b>User managers</b> can invite and remove members, and see all
            members and pending invitations.
            <br />
            <b>Owners</b> can change the payment method, view billing
            information, see receipts, invite and remove members, cancel your
            subscription, and assign or remove owner access.
          </Trans>
        </TextBody>
      ),
    }),
    myPaperTabletIsPairedWithPersonalAccount: () => ({
      id: 'my-paper-tablet-is-paired-with-personal-account',
      title: (
        <Trans>
          My paper tablet is paired with a personal account. How do I join
          another Connect subscription without losing my documents?
        </Trans>
      ),
      body: (
        <TextBody>
          <Trans>
            You don&apos;t need to re-pair your device with a new account to
            join a Connect subscription, just change the email address
            associated with your existing account. On{' '}
            <Link
              inline
              to="https://my.remarkable.com"
              trackingLocation="faq connect-with-seats my-paper-tablet-is-paired-with-personal-account"
            >
              my.remarkable.com
            </Link>
            , click <b>Account</b> in the menu in the upper-left corner. Under
            Contact details, change your email to the one invited to the new
            Connect subscription.
          </Trans>
        </TextBody>
      ),
    }),
    canIManageASubscriptionWithoutPayingForConnectForMyself: () => ({
      id: 'can-i-manage-a-subscription-without-paying-for-connect-for-myself',
      title: (
        <Trans>
          Can I manage a subscription without paying for Connect for myself?
        </Trans>
      ),
      body: (
        <TextBody>
          <Trans>
            Yes. If you don&apos;t need access to subscription features, you can
            choose to deactivate these. To remove subscription features from
            your seat, click the <b>Members</b> tab, then <b>Deactivate</b> next
            to your email address. You&apos;ll then no longer have access to
            unlimited cloud storage, mobile and desktop note-taking, or the
            reMarkable Protection Plan.
          </Trans>
        </TextBody>
      ),
    }),
  },
  enterprise: {
    whatHappensToMyFilesIfIActivateMyEnterpriseAccount: () => ({
      id: 'what-happens-to-my-files-if-i-activate-my-enterprise-account',
      title: (
        <Trans>
          What happens to my files if I activate my enterprise account?
        </Trans>
      ),
      body: (
        <TextBody>
          <Trans>
            No changes are made to the content of your files. When you start
            using your enterprise account, control will be transferred to your
            employer. Your employer will then have administrative control of
            your account, and can delete it, which will erase all its files.
          </Trans>
        </TextBody>
      ),
    }),
    canAnyoneElseGetAccessToMyDocuments: () => ({
      id: 'can-anyone-else-get-access-to-my-documents',
      title: <Trans>Can anyone else get access to my documents?</Trans>,
      body: (
        <TextBody>
          <Trans>
            Even though control of the account is transferred to your employer,
            others will not get access to your documents. However, your employer
            will have administrative control of your account, and can delete it,
            which will erase all its files.
          </Trans>
        </TextBody>
      ),
    }),

    whatHappensToTheTermsAndConditionsForRemarkableAccounts: () => ({
      id: 'what-happens-to-the-terms-and-conditions-for-remarkable-accounts',
      title: (
        <Trans>
          What happens to the Terms and Conditions for reMarkable Accounts that
          I&apos;ve agreed to?
        </Trans>
      ),
      body: (
        <TextBody>
          <Trans>
            If you activate an enterprise account, the terms you&apos;ve agreed
            to will end. The contractual relationship will then be between
            reMarkable and your employer.
          </Trans>
        </TextBody>
      ),
    }),
    whatHappensIfIHaveAFreeConnectTrial: () => ({
      id: 'what-happens-if-i-have-a-free-connect-trial',
      title: <Trans>What happens if I have a free Connect trial?</Trans>,
      body: (
        <TextBody>
          <Trans>
            You&apos;ll keep your free trial. Once your trial ends, your
            employer will pay for your subscription.
          </Trans>
        </TextBody>
      ),
    }),
    iHaveFreeAccessToConnect: () => ({
      id: 'i-have-free-access-to-connect',
      title: (
        <Trans>
          I have free access to Connect. What happens if I join a Connect
          subscription?
        </Trans>
      ),
      body: (
        <TextBody>
          <Trans>
            You&apos;ll still get Connect for free, since the subscription will
            be paid by your employer. If you leave your employer&apos;s
            subscription, you&apos;ll lose free access to Connect.
          </Trans>
        </TextBody>
      ),
    }),
    whatDoesItMeanForMyEmployerToHaveAdministrativeControlOverMyFiles: () => ({
      id: 'what-does-it-mean-for-my-employer-to-have-administrative-control-over-my-files',
      title: (
        <Trans>
          What does it mean for my employer to have &quot;administrative
          control&quot; over my files?
        </Trans>
      ),
      body: (
        <TextBody>
          <Trans>
            If you activate your enterprise account, you can still organize,
            edit, and move files as you like. You can even create a private
            account and transfer files there.
            <br />
            Since your employer owns the Connect Enterprise account, they can
            request deletion of your content and account.
            <br />
            In terms of compliance with privacy and data protection laws, and to
            the extent the General Data Protection Regulation 2016/679 (GDPR)
            applies, this means that your employer will become a controller of
            processed personal data necessary to provide Connect Enterprise, and
            reMarkable will act as a processor. In other words, your employer
            will be able to determine the purposes and means of the personal
            data processing. To the extent the California Consumer Privacy Act
            at Cal. Civ. Code § 1798.100 et seq. and its implementing
            regulations (CCPA) apply, your employer will act as the business and
            reMarkable as the service provider. Terms such as
            “process/processing,” “controller,” “processor,” “business,”
            “service provider,” and “personal data” have the same meaning
            depending on what law applies.
          </Trans>
        </TextBody>
      ),
    }),
  },
  enterpriseEnrollment: {
    canITestSSOSolution: () => ({
      id: 'enterpriseEnrollment.can-i-test-sso-solution',
      title: (
        <Trans>
          Can I test your SSO solution with a small group of users before
          rolling it out to everyone?
        </Trans>
      ),
      body: (
        <TextBody>
          <Trans>
            Yes, you can invite a small group of users to test before applying
            our SSO solution company-wide. These users need to be added in your
            identity provider (IdP) first. The testing phase helps ensure that
            everything is set up correctly for your company.
          </Trans>
        </TextBody>
      ),
    }),
    isThereAutomatedProcessForInvitingUsers: () => ({
      id: 'enterpriseEnrollment.is-there-automated-process-for-inviting-users',
      title: <Trans>Is there an automated process for inviting users?</Trans>,
      body: (
        <TextBody>
          <Trans>
            You can manually invite a small group of users for testing by adding
            them to your identity provider. Or, you can start the migration
            phase, which will automatically include all existing users without
            needing individual invitations.
          </Trans>
        </TextBody>
      ),
    }),
    whatHappensDuringMigrationPhase: () => ({
      id: 'enterpriseEnrollment.what-happens-during-migration-phase',
      title: <Trans>What happens during the migration phase?</Trans>,
      body: (
        <TextBody>
          <Trans>
            The migration phase marks all users with your domain as claimed.
            They&apos;ll get an email about the migration, and a banner appears
            on their accounts asking them to either migrate or update their
            email address. Users can still log in with their old credentials
            while completing the transition.
          </Trans>
        </TextBody>
      ),
    }),
    willUsersStillUseOldCredentials: () => ({
      id: 'enterpriseEnrollment.will-users-still-use-old-credentials',
      title: (
        <Trans>
          Will users still be able to use their old credentials once the
          migration is done?
        </Trans>
      ),
      body: (
        <TextBody>
          <Trans>
            No, once the migration is complete and domain forwarding has been
            activated, users will have to log in with single sign-on. Their
            previous login credentials will no longer work.
          </Trans>
        </TextBody>
      ),
    }),
    howAreUsersInformedAboutMigration: () => ({
      id: 'enterpriseEnrollment.how-are-users-informed-about-migration',
      title: <Trans>How are users informed about the migration process?</Trans>,
      body: (
        <TextBody>
          <Trans>
            They&apos;ll receive an email that explains how the migration
            process works and what to do.
          </Trans>
        </TextBody>
      ),
    }),
    whatHappensToAccountsNotUsingDomain: () => ({
      id: 'enterpriseEnrollment.what-happens-to-accounts-not-using-domain',
      title: (
        <Trans>What happens to accounts that don&apos;t use our domain?</Trans>
      ),
      body: (
        <TextBody>
          <Trans>
            Accounts that aren&apos;t registered on your domain won&apos;t be
            affected by the migration or domain forwarding. These users can keep
            using their current credentials to log in.
          </Trans>
        </TextBody>
      ),
    }),
    whatHappensToFilesWhenActivatingEnterpriseAccount: () => ({
      id: 'enterpriseEnrollment.what-happens-to-files-when-activating-enterprise-account',
      title: (
        <Trans>
          What happens to an employee&apos;s files when they activate their
          enterprise account?
        </Trans>
      ),
      body: (
        <TextBody>
          <Trans>
            No changes are made to the content of their files. Once an employee
            activates their enterprise account, administrative control of their
            account will transfer to you as their employer. You&apos;ll then
            have the ability to manage their account, including deleting it if
            necessary, which will erase all their files.
          </Trans>
        </TextBody>
      ),
    }),
    whatHappensIfEmployeeHasFreeTrial: () => ({
      id: 'enterpriseEnrollment.what-happens-if-employee-has-free-trial',
      title: (
        <Trans>What happens if an employee has a free Connect trial?</Trans>
      ),
      body: (
        <TextBody>
          <Trans>
            Employees keep their free trials. Once the trial ends, your
            organization will handle payment for their subscription.
          </Trans>
        </TextBody>
      ),
    }),
    whatHappensToFreeAccessIfJoinSubscription: () => ({
      id: 'enterpriseEnrollment.what-happens-to-free-access-if-join-subscription',
      title: (
        <Trans>
          What happens to an employee&apos;s free access to Connect if they join
          my subscription?
        </Trans>
      ),
      body: (
        <TextBody>
          <Trans>
            Employees who already have free access to Connect will continue to
            enjoy this benefit under your organization&apos;s subscription, as
            the subscription will now be covered by your company. If they leave
            your subscription, they&apos;ll lose their free access to Connect.
          </Trans>
        </TextBody>
      ),
    }),
  },
  integrations: {
    setupMeetingNotes: () => ({
      title: <Trans>How do I set up the Meeting Notes Tool?</Trans>,
      id: 'integrations.setup-meeting-notes',
      body: (
        <TextBody>
          <Trans>
            The Meeting Notes Tool makes it easy to prepare for meetings by
            creating prefilled notes with details from your calendar. This lets
            you stay present, capture important ideas, and stay organized by
            quickly returning to your notes after meetings.{' '}
            <LinkExternal
              to={SUPPORT_URL.ARTICLES.HOW_TO_USE_MEETING_NOTES}
              inline
              componentLocation={'faq integrations setup-meeting-notes'}
            >
              Learn how set up your calendar integration
            </LinkExternal>
          </Trans>
        </TextBody>
      ),
    }),
    slackPersonalInformation: () => ({
      title: (
        <Trans>
          How does the Slack integration process my personal information?
        </Trans>
      ),
      id: 'integrations.slack-personal-information',
      body: (
        <TextBody>
          <Trans>
            To provide this service, we use OpenAI&apos;s technology to offer
            image to text recognition. To ensure a high level of privacy and
            security, we&apos;ve entered into a privacy agreement with OpenAI
            that regulates our relationship. This agreement sets high security
            standards and ensures that your information will not be used for any
            other purpose other than to provide you with this integration.
            <br />
            In addition, OpenAI can&apos;t use the data for training purposes,
            and we have ensured that only absolutely necessary data to provide
            the service is actually processed. To learn more about how
            reMarkable processes personal information, please visit our{' '}
            <LinkExternal
              to={SUPPORT_URL.PRIVACY_POLICY}
              inline
              componentLocation="faq integrations slack-personal-information"
            >
              Privacy Policy
            </LinkExternal>
            .
          </Trans>
        </TextBody>
      ),
    }),
    setupStorage: () => ({
      title: (
        <Trans>
          How do I set up Google Drive, Slack, Dropbox, and OneDrive?
        </Trans>
      ),
      id: 'integrations.setup-storage',
      body: (
        <TextBody>
          <Trans>
            Improve your workflow by adding integrations to your paper tablet.
            To get started, just follow our step-by-step guide.
            <br />
            <LinkExternal
              to={SUPPORT_URL.ARTICLES.INTEGRATIONS}
              inline
              componentLocation="faq integrations setup-storage"
            >
              Learn how to add integrations
            </LinkExternal>
          </Trans>
        </TextBody>
      ),
    }),
    updateRemarkable: () => ({
      title: <Trans>How can I update my reMarkable?</Trans>,
      id: 'integrations.update-remarkable',
      body: (
        <TextBody>
          <Trans>
            Updating your reMarkable brings you the latest features and software
            improvements — with just a few taps.
            <br />
            <LinkExternal
              to={SUPPORT_URL.ARTICLES.HOW_TO_UPDATE_YOUR_REMARKABLE}
              inline
              componentLocation="faq integrations update-remarkable"
            >
              Learn how to update your device
            </LinkExternal>
          </Trans>
        </TextBody>
      ),
    }),
    supportedFileTypes: () => ({
      title: <Trans>Which file types are supported on reMarkable?</Trans>,
      id: 'integrations.supported-file-types',
      body: (
        <TextBody>
          <Trans>
            You can use PDFs and EPUBs on your reMarkable.
            <br />
            You can also transfer Microsoft Word and PowerPoint files to your
            paper tablet, where you can review and annotate them, with the{' '}
            <LinkExternal
              to={SUPPORT_URL.ARTICLES.FILETYPES_REMARKABLE}
              inline
              componentLocation="faq integrations supported-file-types"
            >
              Read on reMarkable
            </LinkExternal>{' '}
            add-ins.
          </Trans>
        </TextBody>
      ),
    }),
    connectCloud: () => ({
      title: <Trans>How do I connect to the reMarkable cloud?</Trans>,
      id: 'integrations.connect-cloud',
      body: (
        <TextBody>
          <Trans>
            The reMarkable cloud lets you store and sync your files across your
            devices, making them easily accessible. It also serves as a secure
            backup, keeping your documents safely stored.
            <br />
            <LinkExternal
              to={SUPPORT_URL.ARTICLES.PAIR_REMARKABLE_WITH_CLOUD}
              inline
              componentLocation="faq integrations connect-cloud"
            >
              Learn how to use our cloud service
            </LinkExternal>
          </Trans>
        </TextBody>
      ),
    }),
  },
} as const satisfies FAQConfig;
